import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useToast,
} from "@asm/ui";
import {
  ArrowLeft,
  Upload,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Globe,
  AlertTriangle,
  FileText,
  Download,
  Loader2,
} from "lucide-react";
import {
  adminApi,
  ReconCampaign,
  ReconDomain,
  CampaignStats,
  CampaignFinding,
  CampaignResult,
  PaginatedReconDomains,
} from "@/lib/api";

const STATUS_COLORS: Record<string, string> = {
  active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  paused: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  completed: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  archived: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: "bg-red-500/10 text-red-400 border-red-500/20",
  high: "bg-orange-500/10 text-orange-400 border-orange-500/20",
  medium: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  low: "bg-blue-500/10 text-blue-400 border-blue-500/20",
};

interface CampaignDetailProps {
  campaign: ReconCampaign;
  onBack: () => void;
}

export function CampaignDetail({ campaign, onBack }: CampaignDetailProps) {
  const { toast } = useToast();
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [domains, setDomains] = useState<PaginatedReconDomains | null>(null);
  const [findings, setFindings] = useState<CampaignFinding[]>([]);
  const [results, setResults] = useState<{ results: CampaignResult[]; total: number } | null>(null);
  const [domainPage, setDomainPage] = useState(1);
  const [resultPage, setResultPage] = useState(1);
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [exporting, setExporting] = useState(false);

  const perPage = 20;

  useEffect(() => {
    fetchStats();
    fetchDomains();
    fetchFindings();
    fetchResults();
  }, [campaign.id]);

  useEffect(() => {
    fetchDomains();
  }, [domainPage]);

  useEffect(() => {
    fetchResults();
  }, [resultPage]);

  const fetchStats = async () => {
    try {
      const data = await adminApi.getCampaignStats(campaign.id);
      setStats(data);
    } catch (err) {
      console.error("Failed to load campaign stats:", err);
    }
  };

  const fetchDomains = async () => {
    try {
      const data = await adminApi.listCampaignDomains(campaign.id, {
        page: domainPage,
        per_page: perPage,
      });
      setDomains(data);
    } catch (err) {
      console.error("Failed to load campaign domains:", err);
    }
  };

  const fetchFindings = async () => {
    try {
      const data = await adminApi.getCampaignFindings(campaign.id, { per_page: 50 });
      setFindings(data.findings);
    } catch (err) {
      console.error("Failed to load campaign findings:", err);
    }
  };

  const fetchResults = async () => {
    try {
      const data = await adminApi.listCampaignResults(campaign.id, {
        page: resultPage,
        per_page: perPage,
      });
      setResults(data);
    } catch (err) {
      console.error("Failed to load campaign results:", err);
    }
  };

  const handleImport = async () => {
    const domainList = importText
      .split(/[\n,]+/)
      .map((d) => d.trim())
      .filter(Boolean);

    if (!domainList.length) {
      toast({ title: "No Domains", description: "Enter at least one domain.", variant: "destructive" });
      return;
    }

    setImporting(true);
    try {
      const result = await adminApi.importCampaignDomains(campaign.id, domainList, "manual_import");
      toast({
        title: "Import Complete",
        description: `${result.imported} domain(s) imported into campaign.`,
      });
      setImportText("");
      setShowImport(false);
      fetchDomains();
      fetchStats();
    } catch (err) {
      toast({
        title: "Import Failed",
        description: err instanceof Error ? err.message : "Failed to import domains",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  const handleRemoveDomain = async (domain: ReconDomain) => {
    try {
      await adminApi.removeCampaignDomain(campaign.id, domain.id);
      toast({ title: "Domain Removed", description: `${domain.domain} removed from campaign.` });
      fetchDomains();
      fetchStats();
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to remove domain",
        variant: "destructive",
      });
    }
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      await adminApi.exportCampaignCsv(campaign.id, campaign.name);
      toast({ title: "Export Complete", description: "CSV downloaded." });
    } catch (err) {
      toast({
        title: "Export Failed",
        description: err instanceof Error ? err.message : "Failed to export CSV",
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString();
  };

  const domainPages = domains ? Math.ceil(domains.total / perPage) : 0;
  const resultPages = results ? Math.ceil(results.total / perPage) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold">{campaign.name}</h2>
            <Badge variant="outline" className={STATUS_COLORS[campaign.status] || ""}>
              {campaign.status}
            </Badge>
          </div>
          {campaign.description && (
            <p className="text-sm text-muted-foreground mt-1">{campaign.description}</p>
          )}
          <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
            <span className={campaign.sector === "unspecified" ? "italic" : "capitalize"}>
              {campaign.sector === "unspecified" ? "Any sector" : campaign.sector}
            </span>
            <span className={campaign.country_code === "XX" ? "italic" : ""}>
              {campaign.country_code === "XX" ? "Multiple countries" : campaign.country_code}
            </span>
            <span>Priority: {campaign.priority}</span>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExportCsv}
          disabled={exporting || stats?.scanned_count === 0}
        >
          {exporting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Download CSV
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="text-2xl font-bold">{stats.domain_count.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">Domains</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="text-2xl font-bold">{stats.scanned_count.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">Scanned</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="text-2xl font-bold">{stats.total_findings.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">Findings</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="text-2xl font-bold">
                {stats.avg_email_score != null ? Math.round(stats.avg_email_score) : "—"}
              </div>
              <div className="text-xs text-muted-foreground">Avg Email Score</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="domains" className="space-y-4">
        <TabsList className="bg-muted/50 p-1">
          <TabsTrigger value="domains" className="data-[state=active]:bg-background">
            <Globe className="h-4 w-4 mr-1.5" />
            Domains
          </TabsTrigger>
          <TabsTrigger value="findings" className="data-[state=active]:bg-background">
            <AlertTriangle className="h-4 w-4 mr-1.5" />
            Findings
          </TabsTrigger>
          <TabsTrigger value="results" className="data-[state=active]:bg-background">
            <FileText className="h-4 w-4 mr-1.5" />
            Results
          </TabsTrigger>
        </TabsList>

        {/* Domains Tab */}
        <TabsContent value="domains">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Campaign Domains</CardTitle>
                  <CardDescription>{domains?.total ?? 0} domains in this campaign</CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowImport(!showImport)}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Import Domains
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Import panel */}
              {showImport && (
                <div className="mb-4 p-4 rounded-lg border bg-muted/30 space-y-3">
                  <p className="text-sm font-medium">Import domains into this campaign</p>
                  <textarea
                    className="w-full h-24 px-3 py-2 rounded-md border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Enter domains, one per line or comma-separated..."
                    value={importText}
                    onChange={(e) => setImportText(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleImport} disabled={importing}>
                      {importing ? "Importing..." : "Import"}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowImport(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Domain</TableHead>
                      <TableHead>TLD</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Last Scanned</TableHead>
                      <TableHead>Scans</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!domains?.domains.length ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No domains in this campaign yet. Import domains to get started.
                        </TableCell>
                      </TableRow>
                    ) : (
                      domains.domains.map((domain) => (
                        <TableRow key={domain.id}>
                          <TableCell className="font-mono text-sm">{domain.domain}</TableCell>
                          <TableCell>
                            {domain.tld && (
                              <Badge variant="outline" className="text-xs">.{domain.tld}</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">{domain.source}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {formatDate(domain.last_scanned_at)}
                          </TableCell>
                          <TableCell className="font-mono">{domain.scan_count}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveDomain(domain)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {domainPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {domainPage} of {domainPages}
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={domainPage <= 1} onClick={() => setDomainPage(domainPage - 1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" disabled={domainPage >= domainPages} onClick={() => setDomainPage(domainPage + 1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Findings Tab */}
        <TabsContent value="findings">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">High-Severity Findings</CardTitle>
              <CardDescription>Critical and high severity findings from this campaign's scans</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Severity</TableHead>
                      <TableHead>Domain</TableHead>
                      <TableHead>Finding</TableHead>
                      <TableHead>Module</TableHead>
                      <TableHead>Found</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!findings.length ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          No high-severity findings yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      findings.map((f, i) => (
                        <TableRow key={`${f.result_id}-${i}`}>
                          <TableCell>
                            <Badge variant="outline" className={SEVERITY_COLORS[f.severity || ""] || ""}>
                              {f.severity || "unknown"}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-sm">{f.domain}</TableCell>
                          <TableCell>
                            <div>
                              <div className="text-sm font-medium">{f.title}</div>
                              {f.description && (
                                <div className="text-xs text-muted-foreground truncate max-w-[300px]">
                                  {f.description}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">{f.module}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">{formatDate(f.found_at)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Results Tab */}
        <TabsContent value="results">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Scan Results</CardTitle>
              <CardDescription>Recent scan results for this campaign</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Domain</TableHead>
                      <TableHead>Modules</TableHead>
                      <TableHead className="text-right">Email</TableHead>
                      <TableHead className="text-right">Headers</TableHead>
                      <TableHead className="text-right">Tech</TableHead>
                      <TableHead className="text-right">Vulns</TableHead>
                      <TableHead>Completed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!results?.results.length ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No scan results yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      results.results.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell className="font-mono text-sm">{r.domain}</TableCell>
                          <TableCell>
                            <div className="flex gap-1 flex-wrap">
                              {r.modules.slice(0, 3).map((m) => (
                                <Badge key={m} variant="outline" className="text-xs">
                                  {m}
                                </Badge>
                              ))}
                              {r.modules.length > 3 && (
                                <Badge variant="outline" className="text-xs">
                                  +{r.modules.length - 3}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {r.email_security_score ?? "—"}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {r.security_headers_score ?? "—"}
                          </TableCell>
                          <TableCell className="text-right font-mono">{r.technologies_found}</TableCell>
                          <TableCell className="text-right font-mono">{r.vulns_found}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {formatDate(r.completed_at)}
                            {r.partial && (
                              <Badge variant="outline" className="ml-1 text-xs bg-amber-500/10 text-amber-400">
                                partial
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {resultPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {resultPage} of {resultPages}
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={resultPage <= 1} onClick={() => setResultPage(resultPage - 1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" disabled={resultPage >= resultPages} onClick={() => setResultPage(resultPage + 1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
